import React from 'react';
import { X } from 'lucide-react';

/**
 * MultiSelectChips - Multi-select chip component with maximum limit
 * Used for skills, availability, locations, and work types selection
 * 
 * @param {Array} options - Array of {value, label} objects
 * @param {Array} value - Array of selected values
 * @param {Function} onChange - Callback when selection changes
 * @param {number} max - Maximum number of selections allowed
 * @param {string} testId - Base data-testid for testing
 * @param {string} placeholder - Placeholder text when nothing selected
 * @param {boolean} disabled - Disable all interactions
 */
export const MultiSelectChips = ({ 
  options = [], 
  value = [], 
  onChange, 
  max = 8,
  testId = 'chips',
  placeholder = 'Select options',
  disabled = false
}) => {
  const handleToggle = (optionValue) => {
    if (disabled) return;
    
    const isSelected = value.includes(optionValue);
    
    if (isSelected) {
      // Remove from selection
      const newValue = value.filter(v => v !== optionValue);
      onChange?.(newValue);
    } else {
      // Add to selection only if under max limit
      if (value.length < max) {
        const newValue = [...value, optionValue];
        onChange?.(newValue);
      }
    }
  };
  
  const handleClearAll = () => {
    if (disabled) return;
    onChange?.([]);
  };
  
  const isMaxReached = value.length >= max;
  
  return (
    <div className="space-y-3" data-testid={testId}>
      {/* Selection counter */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        {max && (
          <span className={`font-medium ${isMaxReached ? 'text-warning' : 'text-muted-foreground'}`}>
            {value.length}/{max}
          </span>
        )}
      </div>
      
      {/* Chips container */}
      <div className="flex flex-wrap gap-2" data-testid={`${testId}-container`}>
        {options.map((option) => {
          const isSelected = value.includes(option.value);
          const canSelect = !isSelected && !isMaxReached;
          
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleToggle(option.value)}
              disabled={disabled || (!isSelected && isMaxReached)}
              className={`
                inline-flex items-center gap-1.5 rounded-full px-3 h-9 
                border transition-colors duration-150 text-sm font-medium
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                disabled:opacity-50 disabled:cursor-not-allowed
                ${isSelected 
                  ? 'bg-primary text-primary-foreground border-transparent hover:bg-primary/90' 
                  : canSelect 
                    ? 'bg-secondary text-secondary-foreground border-border hover:bg-secondary/80'
                    : 'bg-muted text-muted-foreground border-border cursor-not-allowed'
                }
              `}
              data-testid={`chip-${option.value}`}
              aria-pressed={isSelected}
            >
              <span>{option.label}</span>
              {isSelected && (
                <X size={14} className="opacity-70" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
      
      {/* Clear all button */}
      {value.length > 0 && (
        <button
          type="button"
          onClick={handleClearAll}
          disabled={disabled}
          className="text-sm text-muted-foreground hover:text-foreground underline transition-colors"
          data-testid={`${testId}-clear-all`}
        >
          Clear all
        </button>
      )}
      
      {/* Max reached warning */}
      {isMaxReached && (
        <p className="text-xs text-warning flex items-center gap-1" data-testid={`${testId}-max-warning`}>
          <span className="font-semibold">Maximum reached.</span>
          <span>Deselect to choose others.</span>
        </p>
      )}
    </div>
  );
};

export default MultiSelectChips;
